import { useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { TransactionsController } from "../../../../api/src/transactions/transactions.controller";
import TransactionsTable, {
  useAllTransactions,
  type TransactionsExportState,
} from "../../components/TransactionsTable";
import { useAuthenticatedQuery } from "../../lib/api";
import { processFileResponse } from "../../lib/file";

/**
 * The account's transactions, from every page of
 * `GET /v1/transactions/account/:id` (APP-1652). The export
 * (`/transactions/account/:id/export`) is not admitted and stays unversioned.
 */
export function useQueryAccountTransactions(accountId: number) {
  return useAllTransactions(`/v1/transactions/account/${accountId}`);
}

/**
 * `/transactions/account/:id/export` with the state of the table (APP-1771): the
 * sweep switch always, and each column filter only with a value. Type and Sub
 * Type go as labels, repeated for each one. The tab has no Household column,
 * no dates and no tick box, so none of those is sent.
 */
export function accountTransactionsExportUrl(
  accountId: number,
  { excludeSweep, filters }: TransactionsExportState,
) {
  const params = new URLSearchParams({ "exclude-sweep": String(excludeSweep) });
  filters.type.forEach((label) => params.append("type", label));
  filters.subType.forEach((label) => params.append("sub-type", label));
  if (filters.symbol) params.append("symbol", filters.symbol);
  if (filters.description) params.append("description", filters.description);
  return `/transactions/account/${accountId}/export?${params.toString()}`;
}

export function useQueryExportAccountTransactions(accountId: number) {
  // Read when the request is built, so the key (and the cache) stays one.
  const url = useRef(
    accountTransactionsExportUrl(accountId, {
      excludeSweep: false,
      filters: { type: [], subType: [] },
    }),
  );
  const query = useAuthenticatedQuery<
    UnpackResponse<TransactionsController["exportAccountTransactions"]>,
    void,
    [string, string, number, string]
  >({
    queryKey: ["transactions", "account", accountId, "export"],
    urlBuilderFn: () => url.current,
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
  const { refetch } = query;
  const exportWith = useCallback(
    (state: TransactionsExportState) => {
      url.current = accountTransactionsExportUrl(accountId, state);
      return refetch();
    },
    [accountId, refetch],
  );
  return { query, exportWith };
}

/**
 * Remounts the list per account, so the rows the page walk collected for one
 * account are never shown under another (`lib/pagination.ts`).
 */
const AccountTransactions = () => {
  const { accountId: accountIdStr } = useParams();
  const accountId = parseInt(accountIdStr ?? "");

  return <AccountTransactionsOfAccount key={accountId} accountId={accountId} />;
};

const AccountTransactionsOfAccount = ({ accountId }: { accountId: number }) => {
  const {
    isPending: isPendingTransactions,
    isError: isErrorTransactions,
    data: transactions,
  } = useQueryAccountTransactions(accountId);

  const { query: queryExport, exportWith } =
    useQueryExportAccountTransactions(accountId);

  return (
    <TransactionsTable
      isLoading={isPendingTransactions}
      isError={isErrorTransactions}
      transactions={transactions ?? []}
      queryExport={queryExport}
      onExport={exportWith}
    />
  );
};

export default AccountTransactions;
